/**
 * DragTextQuiz Adult Renderer
 * Thin rendering component that delegates logic to useDragTextQuiz hook.
 */

import { useMemo } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText, QuizResultFeedback, SubmitButton, QUIZ_INLINE_STYLES, QUIZ_CHIP_STYLES, QuizSolveDescription, QuizHeader } from '../shared'
import { formatProblem } from '../../../utils/quiz-parser'
import type { DragTextQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { useDragTextQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { XCircle } from 'lucide-react'

interface DragTextQuizProps {
  quizId: string | number
  quiz: DragTextQuizData & { choices?: { key?: string | null; answer: string }[]; answers?: Array<string | { key?: string | null; answer: string }> }
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[] // 제출된 답안 (부모에서 전달, 채점 후에도 유지)
  submitLabel?: string // 제출 버튼 라벨 (default: '정답 확인')
  hideSubmitButton?: boolean // 제출 버튼 숨김 (체크업 등에서 외부 버튼 사용 시)
  onAnswerChange?: (answers: string[]) => void // 답변 변경 시 콜백
  disableSound?: boolean // 사운드 비활성화 (리뷰 모드 등)
  difficultyLevel?: number | null // 난이도 (1-5)
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  blankPlaceholder?: string
}

export function DragTextQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  blankPlaceholder,
}: DragTextQuizProps) {
  const { t } = useTranslation('quiz')

  const {
    answers,
    currentIndex,
    blankCount,
    problemKey,
    availableOptions,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolveLabel,
    handleBlankClick,
    handleClearBlank,
    handleSelectOption,
    handleSubmit,
  } = useDragTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // 문제 렌더링
  const renderedProblem = useMemo(() => {
    if (answers.length === 0 && blankCount > 0) return []

    const timestamp = problemKey
    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${timestamp}`,
      (index: number) => {
        const value = answers[index] || ''
        const valueLabel = resolveLabel(value)
        const isActive = currentIndex === index && !isSolved && !isViewSolved
        const hasValue = value !== ''
        const isCorrect = isSolved ? solveResult?.corrects?.[index] : null
        const correctAnswer = solveResult?.correctAnswers?.[index] ?? ''
        const correctAnswerLabel = resolveLabel(correctAnswer)

        return (
          <span
            key={`blank-${quizId}-${timestamp}-${index}`}
            onClick={() => handleBlankClick(index)}
            className={cn(
              QUIZ_INLINE_STYLES.base,
              QUIZ_INLINE_STYLES.box,
              'border transition-all duration-200 min-w-0',
              // 기본 상태 (빈 박스)
              !isSolved && !isActive && !hasValue && 'border-gray-300',
              !isSolved && !isActive && !hasValue && QUIZ_INLINE_STYLES.boxEmpty,
              // 활성 상태
              isActive && !hasValue && 'border-blue-500 ring-2 ring-blue-100',
              // 값이 있는 상태 (filled)
              !isSolved && hasValue && QUIZ_INLINE_STYLES.boxFilled,
              // 정답
              isSolved && isCorrect === true && QUIZ_INLINE_STYLES.correct,
              // 오답 (정답보기 모드가 아닐 때만 빨강)
              isSolved && isCorrect === false && !shouldShowCorrectAnswers && QUIZ_INLINE_STYLES.wrong,
              // 정답 표시 모드 (오답인 항목을 정답으로 표시 - 초록색)
              shouldShowCorrectAnswers && isSolved && isCorrect === false && QUIZ_INLINE_STYLES.correct,
              (isSolved || isViewSolved) && 'cursor-not-allowed'
            )}
          >
            {shouldShowCorrectAnswers && isSolved && isCorrect === false ? (
              <KatexText text={correctAnswerLabel} />
            ) : hasValue ? (
              <span className="flex gap-1 items-center">
                <KatexText text={valueLabel} />
                {!isSolved && !isViewSolved && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleClearBlank(index)
                    }}
                    className="ml-1 p-0.5 rounded-xs hover:bg-blue-200 transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5 text-gray-400 hover:text-red-400" />
                  </button>
                )}
              </span>
            ) : (
              <span className={QUIZ_INLINE_STYLES.boxEmpty}>{blankPlaceholder ?? t('types.dragText.blank')}</span>
            )}
          </span>
        )
      },
      quiz.additionalImages ?? undefined
    )
  }, [answers, currentIndex, isSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, blankCount, handleBlankClick, handleClearBlank, problemKey, quiz.additionalImages, resolveLabel, blankPlaceholder, t])

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.dragText.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
      />

      {/* 문제 본문 - 기획 데모 .passage-area 스타일 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          'p-4 rounded-xl bg-gray-100',
          'text-xl text-gray-700',
          '[&_.formatProblem]:leading-[1.8]',
          '[&_span[class*="inline-flex"]]:leading-normal'
        )}
      >
        {renderedProblem}
      </motion.div>

      {/* 보기 버튼 (quiz.answers) - 기획 데모 .box-chip 스타일 */}
      {!isSolved && !isViewSolved && availableOptions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3"
        >
          <p className="text-base font-semibold text-gray-600">
            {t('types.dragText.selectFromChoices')}
          </p>
          <div className="flex flex-wrap gap-2 text-lg">
            {availableOptions.map((option, idx) => {
              return (
                <motion.button
                  key={option.key || `option-${idx}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => handleSelectOption(option.key)}
                  className={cn(
                    QUIZ_CHIP_STYLES.base,
                    QUIZ_CHIP_STYLES.hover,
                  )}
                >
                  <KatexText text={option.label} />
                </motion.button>
              )
            })}
          </div>
        </motion.div>
      )}

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 결과 피드백 */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          correctAnswer={shouldShowCorrectAnswers ? solveResult?.correctAnswers?.map(a => resolveLabel(a)).join(', ') : undefined}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* 해설 */}
      {isSolved && solveResult?.description && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult.description}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}
